import React, { useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Gauge, Paperclip, CheckCircle2, AlertTriangle, MessageSquare, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export const KpiReviewDialog = ({ kpi, canRespond, onOpenChange, onDone }) => {
  const [mode, setMode] = useState(null); // null | "ack" | "dispute"
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState(kpi);
  const [threadText, setThreadText] = useState("");
  const [posting, setPosting] = useState(false);

  const postThread = async () => {
    if (!threadText.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/kpi/${rec.id}/comments`, { text: threadText.trim() });
      setRec(r.data);
      setThreadText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to post comment"); }
    finally { setPosting(false); }
  };

  const download = async (a) => {
    try {
      const resp = await fetch(`${API_BASE}/kpi/${kpi.id}/attachments/${a.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const submit = async () => {
    if (!comment.trim()) { toast.error("A comment is required before submitting."); return; }
    setBusy(true);
    try {
      if (mode === "ack") {
        await api.post(`/kpi/${kpi.id}/acknowledge`, { comment });
        toast.success("Marked fixed / acknowledged");
      } else {
        await api.post(`/kpi/${kpi.id}/request-correction`, { comment });
        toast.success("Dispute sent back to " + kpi.created_by_name);
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="kpi-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Gauge className="w-5 h-5 text-summit-primary" /> KPI correction — {kpi.date}
          </DialogTitle>
          <DialogDescription>Raised by {kpi.created_by_name}. A comment is required whether you acknowledge or dispute.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {kpi.category && (
            <span className="inline-flex px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-mono text-[10px] uppercase tracking-widest" data-testid="kpi-review-category">{kpi.category}</span>
          )}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Errors found / to fix</div>
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap" data-testid="kpi-review-errors">{kpi.errors}</p>
          </div>
          {(kpi.attachments || []).length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Attachments</div>
              <div className="mt-1 space-y-1">
                {kpi.attachments.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-xs text-gray-600">
                    <Paperclip className="w-3 h-3 shrink-0 text-gray-400" />
                    <button type="button" className="truncate font-medium hover:underline" onClick={() => download(a)} data-testid={`kpi-review-attach-dl-${a.id}`}>{a.filename}</button>
                    <span className="text-gray-400">{Math.round(a.size / 1024)} KB</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-500">
              <MessageSquare className="w-3 h-3" /> Comments
            </div>
            {(rec.comments || []).length > 0 && (
              <div className="mt-1.5 space-y-1.5 max-h-40 overflow-y-auto" data-testid="kpi-review-comments">
                {(rec.comments || []).map((c) => (
                  <div key={c.id} className="rounded-md px-3 py-2 border bg-gray-50 border-summit-hairline">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      {c.by_name}{c.at ? ` · ${new Date(c.at).toLocaleString()}` : ""}
                    </div>
                    <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap">{c.text}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-1.5 flex items-start gap-2">
              <Textarea rows={1} className="min-h-[38px] text-sm" value={threadText} onChange={(e) => setThreadText(e.target.value)} placeholder="Write a comment…" data-testid="kpi-review-comment-input" />
              <Button size="sm" className="h-9 bg-summit-primary hover:bg-summit-primary-soft text-white shrink-0" onClick={postThread} disabled={posting || !threadText.trim()} data-testid="kpi-review-comment-post">
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          {mode && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">
                {mode === "ack" ? "Your comment (what you fixed / your response) · required" : "Why are you disputing this? · required"}
              </Label>
              <Textarea className="mt-1.5" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={mode === "ack" ? "e.g. Signed jobcard 4587 step 12 and corrected the timesheet…" : "Explain why this is incorrect…"} data-testid="kpi-correction-input" />
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {canRespond && !mode && (
            <>
              <Button variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800" onClick={() => setMode("dispute")} disabled={busy} data-testid="kpi-request-correction-btn">
                <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Dispute
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setMode("ack")} disabled={busy} data-testid="kpi-acknowledge-btn">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Fixed / Acknowledged
              </Button>
            </>
          )}
          {canRespond && mode && (
            <>
              <Button variant="outline" onClick={() => setMode(null)} disabled={busy}>Back</Button>
              <Button
                className={mode === "ack" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
                onClick={submit}
                disabled={busy}
                data-testid="kpi-response-submit"
              >
                {mode === "ack" ? "Submit & complete" : "Send dispute"}
              </Button>
            </>
          )}
          {!canRespond && <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
